import React from 'react';
import {
  Dashboard as DashboardIcon,
  BookOnline as BookOnlineIcon,
  DirectionsBike as DirectionsBikeIcon,
  SportsHandball as SportsHandballIcon,
  SelfImprovement as SelfImprovementIcon,
  Sports as SportsIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Games as AmenitiesIcon,
  FitnessCenter as PersonalTrainingIcon,  
  Event as EventIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  SportsGymnastics as ClassesIcon,
  Business as ClubsIcon,
  Discount as DiscountIcon,
  Groups as GroupsIcon,
  AttachMoney as AttachMoneyIcon,
  Article as ArticleIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  SportsKabaddi as CoachingIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
  AdminPanelSettings as AdminIcon,
  LocationOn as LocationIcon,
  BarChart as ReportIcon,
  CardGiftcard as GuestPassIcon,
  Person as PersonIcon,
  AddBox as AddOnIcon,
  DonutSmall as DonutSmallIcon,
  Paid as PaidIcon,
  Help as HelpIcon,
  Note as NoteIcon,
  ShoppingCart as ShoppingCartIcon,
  NotificationsActive as NotificationsActiveIcon,
  Redeem as RedeemIcon,
  Restaurant as RestaurantIcon,
  Fastfood as FastfoodIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { UserRole } from '../../types';
import { User } from 'lucide-react';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: UserRole[];
}

export interface NavSection {
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
}

export interface RoleNavigation {
  standaloneItems: NavItem[];
  sections: NavSection[];
}

// Super Admin Navigation
export const superAdminNavigation: RoleNavigation = {
  standaloneItems: [],
  sections: [
    {
      label: 'Platform',
      icon: <AdminIcon />,
      items: [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: <DashboardIcon />,
          roles: [UserRole.SUPER_ADMIN]
        },
        {
          label: 'Clubs',
          path: '/clubs',
          icon: <ClubsIcon />,
          roles: [UserRole.SUPER_ADMIN]
        },
        {
          label: 'User Management',
          path: '/user-management',
          icon: <PeopleIcon />,
          roles: [UserRole.SUPER_ADMIN]
        },
        {
          label: 'Notifications',
          path: '/system-notifications',
          icon: <NotificationsActiveIcon />,
          roles: [UserRole.SUPER_ADMIN]
        },
        {
          label: 'Settings',
          path: '/settings',
          icon: <SettingsIcon />,
          roles: [UserRole.SUPER_ADMIN]
        }
      ]
    },
    {
      label: 'Membership',
      icon: <PeopleIcon />,
      items: [
        {
          label: 'Members',
          path: '/member-management',
          icon: <PeopleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Applications',
          path: '/membership-applications',
          icon: <ArticleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Membership Tiers',
          path: '/membership-tiers',
          icon: <PaymentIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Groups',
          path: '/groups',
          icon: <GroupsIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'One-Time Fees',
          path: '/one-time-fees',
          icon: <AttachMoneyIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Add-Ons',
          path: '/add-ons',
          icon: <AddOnIcon />,
          roles: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE]
        },
        {
          label: 'Gift Cards',
          path: '/gift-cards',
          icon: <RedeemIcon />,
          roles: [UserRole.ADMIN]
        }
      ]
    },
    {
      label: 'Transactions',
      icon: <PeopleIcon />,
      items: [
        {
          label: 'All Transactions',
          path: '/transactions',
          icon: <PeopleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Applications',
          path: '/membership-applications',
          icon: <ArticleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Membership Tiers',
          path: '/membership-tiers',
          icon: <PaymentIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Groups',
          path: '/groups',
          icon: <GroupsIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'One-Time Fees',
          path: '/one-time-fees',
          icon: <AttachMoneyIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Add-Ons',
          path: '/add-ons',
          icon: <AddOnIcon />,
          roles: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE]
        }
      ]
    },
    {
      label: 'Reporting',
      icon: <ReportIcon />,
      items: [
        {
          label: 'Management',
          path: '/management-report',
          icon: <DonutSmallIcon />,
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN]
        },
        {
          label: 'Coaching',
          path: '/coaching-report',
          icon: <ArticleIcon />,
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN]
        },
        {
          label: 'Financials',
          path: '/reports',
          icon: <DashboardIcon />,
          roles: [UserRole.SUPER_ADMIN]
        },
        {
          label: 'Audit Logs',
          path: '/audit-logs',
          icon: <ArticleIcon />,
          roles: [UserRole.SUPER_ADMIN]
        }
      ]
    }
  ]
};

// Admin Navigation
export const adminNavigation: RoleNavigation = {
  standaloneItems: [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: <DashboardIcon />,
      roles: [UserRole.ADMIN]
    },
    {
      label: 'Calendar',
      path: '/calendar',
      icon: <CalendarViewMonthIcon />,
      roles: [UserRole.ADMIN]
    },
    {
      label: 'Kiosk Mode',
      path: '/kiosk',
      icon: <User />,
      roles: [UserRole.ADMIN]
    }
  ],
  sections: [
    {
      label: 'Membership',
      icon: <PeopleIcon />,
      items: [
        {
          label: 'Members',
          path: '/member-management',
          icon: <PeopleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Applications',
          path: '/membership-applications',
          icon: <ArticleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Membership Tiers',
          path: '/membership-tiers',
          icon: <PaymentIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Groups',
          path: '/groups',
          icon: <GroupsIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'One-Time Fees',
          path: '/one-time-fees',
          icon: <AttachMoneyIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Add-Ons',
          path: '/add-ons',
          icon: <AddOnIcon />,
          roles: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE]
        }
      ]
    },
    {
      label: 'Classes & Training',
      icon: <ClassesIcon />,
      items: [
        {
          label: 'Class Management',
          path: '/class-management',
          icon: <CalendarViewMonthIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Personal Training',
          path: '/personal-trainings',
          icon: <PersonalTrainingIcon />,
          roles: [UserRole.ADMIN, UserRole.COACH]
        },
        {
          label: 'Coaching',
          path: '/coaching',
          icon: <CoachingIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Coaching Sessions',
          path: '/coaching/sessions',
          icon: <ScheduleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Coaching Programs',
          path: '/coaching/programs',
          icon: <RepeatIcon />,
          roles: [UserRole.ADMIN]
        }, 
        {
          label: 'Group Play',
          path: '/group-plays',
          icon: <SportsIcon />,
          roles: [UserRole.ADMIN]
        }
      ]
    },
    {
      label: 'Facilities & Events',
      icon: <LocationIcon />,
      items: [
        {
          label: 'Amenities',
          path: '/amenities',
          icon: <AmenitiesIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Equipment',
          path: '/equipment',
          icon: <ShoppingCartIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Courts Calendar',
          path: '/courts-calendar',
          icon: <ScheduleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Event Management',
          path: '/event-management',
          icon: <CalendarViewMonthIcon />,
          roles: [UserRole.ADMIN]
        }
      ]
    },
    {
      label: 'Administration',
      icon: <AdminIcon />,
      items: [
        {
          label: 'User Management',
          path: '/user-management',
          icon: <PeopleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Transactions',
          path: '/transactions',
          icon: <PaymentIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Discounts',
          path: '/discounts',
          icon: <DiscountIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Guest Passes',
          path: '/guest-passes',
          icon: <GuestPassIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Gift Cards',
          path: '/gift-cards',
          icon: <RedeemIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Settings',
          path: '/settings',
          icon: <SettingsIcon />,
          roles: [UserRole.ADMIN]
        }
      ]

    },
    {
      label: 'Reporting',
      icon: <ReportIcon />,
      items: [
        {
          label: 'Management',
          path: '/management-report',
          icon: <DonutSmallIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Coaching',
          path: '/coaching-report',
          icon: <ArticleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Financial',
          path: '/financial-report',
          icon: <PaidIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Accounting',
          path: '/accounting-report',
          icon: <PaidIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Accounting - Drill Down',
          path: '/accounting-drill-down-report',
          icon: <PaidIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Arrears Report',
          path: '/arrears-report',
          icon: <PaidIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'F&B Financial',
          path: '/fnb-financial-report',
          icon: <RestaurantIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'F&B Item Sales',
          path: '/fnb-item-sales-report',
          icon: <FastfoodIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Shop Financial',
          path: '/shop-financial-report',
          icon: <StoreIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Court Utilization',
          path: '/court-utilization-report',
          icon: <ReportIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Member Activity',
          path: '/member-activity-report',
          icon: <PeopleIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Audit Logs',
          path: '/audit-logs',
          icon: <ArticleIcon />,
          roles: [UserRole.ADMIN]
        }
      ]
    },
    {
      label: 'Support',
      icon: <ClubsIcon />,
      items: [
        {
          label: 'FAQ',
          path: '/support',
          icon: <HelpIcon />,
          roles: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE]
        }
      ]
    }
  ]
};

// Employee Navigation
export const employeeNavigation: RoleNavigation = {
  standaloneItems: [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: <DashboardIcon />,
      roles: [UserRole.EMPLOYEE]
    },
    {
      label: 'Calendar',
      path: '/calendar',
      icon: <CalendarViewMonthIcon />,
      roles: [UserRole.EMPLOYEE]
    },
    {
      label: 'Kiosk Mode',
      path: '/kiosk',
      icon: <User />,
      roles: [UserRole.ADMIN]
    }
  ],
  sections: [
    {
      label: 'Membership',
      icon: <PeopleIcon />,
      items: [
        {
          label: 'Members',
          path: '/member-management',
          icon: <PeopleIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Applications',
          path: '/membership-applications',
          icon: <ArticleIcon />,
          roles: [UserRole.EMPLOYEE]
        }
      ]
    },
    {
      label: 'Classes & Training',
      icon: <ClassesIcon />,
      items: [
        {
          label: 'Classes',
          path: '/classes',
          icon: <ClassesIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Class Management',
          path: '/class-management',
          icon: <CalendarViewMonthIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Personal Training',
          path: '/personal-trainings',
          icon: <PersonalTrainingIcon />,
          roles: [UserRole.EMPLOYEE, UserRole.COACH]
        },
        {
          label: 'Coaching',
          path: '/coaching',
          icon: <CoachingIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Coaching Sessions',
          path: '/coaching/sessions',
          icon: <ScheduleIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Coaching Programs',
          path: '/coaching/programs',
          icon: <RepeatIcon />,
          roles: [UserRole.EMPLOYEE]
        }
      ]
    },
    {
      label: 'Facilities & Events',
      icon: <LocationIcon />,
      items: [
        {
          label: 'Amenities',
          path: '/amenities',
          icon: <AmenitiesIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Equipment',
          path: '/equipment',
          icon: <ShoppingCartIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Courts Calendar',
          path: '/courts-calendar',
          icon: <ScheduleIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Events',
          path: '/events',
          icon: <EventIcon />,
          roles: [UserRole.EMPLOYEE]
        }
      ]
    },
    {
      label: 'Administration',
      icon: <AdminIcon />,
      items: [
        {
          label: 'Payments',
          path: '/payments',
          icon: <PaymentIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Guest Passes',
          path: '/guest-passes',
          icon: <GuestPassIcon />,
          roles: [UserRole.EMPLOYEE]
        }
      ]
    },
    {
      label: 'Support',
      icon: <ClubsIcon />,
      items: [
        {
          label: 'FAQ',
          path: '/support',
          icon: <HelpIcon />,
          roles: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE]
        }
      ]
    }
  ]
};

// Coach Navigation
export const coachNavigation: RoleNavigation = {
  standaloneItems: [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: <DashboardIcon />,
      roles: [UserRole.COACH]
    },
    {
      label: 'Calendar',
      path: '/calendar',
      icon: <CalendarViewMonthIcon />,
      roles: [UserRole.COACH]
    },
    {
      label: 'Kiosk Mode',
      path: '/kiosk',
      icon: <User />,
      roles: [UserRole.ADMIN]
    }
  ],
  sections: [
    {
      label: 'My Coaching',
      icon: <CoachingIcon />,
      items: [
        {
          label: 'Coaching Dashboard',
          path: '/coaching',
          icon: <CoachingIcon />,
          roles: [UserRole.COACH]
        },
        {
          label: 'Coaching Sessions',
          path: '/coaching/sessions',
          icon: <ScheduleIcon />,
          roles: [UserRole.COACH]
        },
        {
          label: 'Programs/Clinics',
          path: '/coaching/programs',
          icon: <RepeatIcon />,
          roles: [UserRole.COACH]
        },
        {
          label: 'Classes',
          path: '/class-management',
          icon: <CalendarViewMonthIcon />,
          roles: [UserRole.ADMIN]
        },
        {
          label: 'Personal Training',
          path: '/personal-trainings',
          icon: <PersonalTrainingIcon />,
          roles: [UserRole.COACH]
        },
           {
          label: 'Group Play',
          path: '/group-plays',
          icon: <SportsIcon />,
          roles: [UserRole.COACH]
        },
      ]
    },
    {
      label: 'Facilities & Events',
      icon: <LocationIcon />,
      items: [
        {
          label: 'Amenities',
          path: '/amenities',
          icon: <AmenitiesIcon />,
          roles: [UserRole.COACH]
        },
        {
          label: 'Equipment',
          path: '/equipment',
          icon: <ShoppingCartIcon />,
          roles: [UserRole.COACH]
        },
        {
          label: 'Courts Calendar',
          path: '/courts-calendar',
          icon: <ScheduleIcon />,
          roles: [UserRole.COACH]
        }
      ]
    },

    {
      label: 'Membership',
      icon: <PeopleIcon />,
      items: [
        {
          label: 'Members',
          path: '/member-management',
          icon: <PeopleIcon />,
          roles: [UserRole.EMPLOYEE]
        },
        {
          label: 'Applications',
          path: '/membership-applications',
          icon: <ArticleIcon />,
          roles: [UserRole.EMPLOYEE]
        },
      ]
    },
    {
      label: 'Support',
      icon: <ClubsIcon />,
      items: [
        {
          label: 'FAQ',
          path: '/support',
          icon: <HelpIcon />,
          roles: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE]
        }
      ]
    }
  ]
};

// Member Navigation
export const memberNavigation: RoleNavigation = {
  standaloneItems: [
    {
      label: 'Home',
      path: '/dashboard',
      icon: <DashboardIcon />,
      roles: [UserRole.MEMBER]
    },
    {
      label: 'Calendar',
      path: '/calendar',
      // path: '/coming-soon',
      icon: <BookOnlineIcon />,
      roles: [UserRole.MEMBER]
    },
    {
      label: 'Classes',
      path: '/classes',
      // path: '/coming-soon',
      icon: <DirectionsBikeIcon />,
      roles: [UserRole.MEMBER]
    },
    {
      label: 'Facilities',
      path: '/amenities/book',
      // path: '/coming-soon',
      icon: <SportsHandballIcon />,
      roles: [UserRole.MEMBER]
    },
    {
      label: 'Group Play',
      path: '/member/group-play',
      // path: '/coming-soon',
      icon: <SportsIcon />,
      roles: [UserRole.MEMBER]
    },
    {
      label: 'Programs/Clinics',
      path: '/member/coaching-programs',
      // path: '/coming-soon',
      icon: <CoachingIcon />,
      roles: [UserRole.MEMBER]
    },
    {
      label: 'Events',
      path: '/events',
      // path: '/coming-soon',
      icon: <EventIcon />,
      roles: [UserRole.MEMBER]
    },
    {
      label: 'Billing',
      path: '/billing',
      icon: <ReceiptIcon />,
      roles: [UserRole.MEMBER]
    },
    {
      label: 'Documents',
      path: '/documents',
      icon: <NoteIcon />,
      roles: [UserRole.MEMBER]
    },


  ],
  sections: [
    // {
    //   label: 'Lessons',
    //   icon: <ClassesIcon />,
    //   items: [

    //   ]
    // },
    // {
    //   label: 'Facilities & Events',
    //   icon: <LocationIcon />,
    //   items: [
    //     {
    //       label: 'Amenities',
    //       path: '/amenities',
    //       icon: <FitnessCenterIcon />,
    //       roles: [UserRole.MEMBER]
    //     },
    //     {
    //       label: 'Events',
    //       path: '/events',
    //       icon: <EventIcon />,
    //       roles: [UserRole.MEMBER]
    //     }
    //   ]
    // }
  ]
};

// Guest Navigation — limited to their own activity pages
export const guestNavigation: RoleNavigation = {
  standaloneItems: [
    {
      label: 'Upcoming Bookings',
      path: '/amenities/book',
      icon: <BookOnlineIcon />,
      roles: [UserRole.GUEST]
    },
    {
      label: 'My Profile',
      path: '/member-profile',
      icon: <PersonIcon />,
      roles: [UserRole.GUEST]
    },
    {
      label: 'Transactions',
      path: '/billing',
      icon: <ReceiptIcon />,
      roles: [UserRole.GUEST]
    },
    {
      label: 'Payment Method',
      path: '/billing',
      icon: <PaymentIcon />,
      roles: [UserRole.GUEST]
    },
  ],
  sections: []
};

// Navigation lookup function
export const getNavigationForRole = (role: UserRole): RoleNavigation => {
  switch (role) {
    case UserRole.SUPER_ADMIN:
      return superAdminNavigation;
    case UserRole.ADMIN:
      return adminNavigation;
    case UserRole.EMPLOYEE:
      return employeeNavigation;
    case UserRole.COACH:
      return coachNavigation;
    case UserRole.MEMBER:
      return memberNavigation;
    case UserRole.GUEST:
      return guestNavigation;
    default:
      return memberNavigation; // fallback
  }
};